import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { KpiCard } from "@/components/dashboard/shared/KpiCard";
import { KpiGrid } from "@/components/dashboard/shared/KpiGrid";
import { InventoryList } from "@/components/dashboard/inventory/InventoryList";
import { InventoryHistory } from "@/components/dashboard/inventory/InventoryHistory";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  RiAlertLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiStackLine,
  RiStore3Line,
} from "@remixicon/react";

type InventorySummary = {
  totalSkus: number;
  totalUnits: number;
  lowStock: number;
  outOfStock: number;
  inStock: number;
};

export function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<InventorySummary | undefined>();
  const [loading, setLoading] = useState(true);
  const activeStatus = searchParams.get("stockStatus") || "all";
  const tab = searchParams.get("tab") === "history" ? "history" : "stock";

  const handleSummaryChange = useCallback(
    (next: InventorySummary | undefined, isLoading: boolean) => {
      setSummary(next);
      setLoading(isLoading);
    },
    [],
  );

  const setStatus = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "stock");
    if (value === "all") next.delete("stockStatus");
    else next.set("stockStatus", value);
    setSearchParams(next, { replace: true });
  };

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "history") next.set("tab", "history");
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Inventory" />
      <PageHeader
        title="Inventory"
        subtitle="Search a product, then record restocks, reductions, and other stock updates."
        actions={
          <Button
            asChild
            className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
          >
            <Link to="/dashboard/pos">
              <RiStore3Line className="h-4 w-4" />
              POS Mode
            </Link>
          </Button>
        }
      />

      <KpiGrid loading={loading} skeletonCount={4}>
        <button
          type="button"
          onClick={() => setStatus("all")}
          className={cn(
            "w-full min-w-0 text-left",
            activeStatus === "all" && tab === "stock" && "ring-2 ring-primary/20 rounded-[1.25rem]",
          )}
        >
          <KpiCard label="Units on hand" value={summary?.totalUnits ?? 0} icon={<RiStackLine />} />
        </button>
        <button
          type="button"
          onClick={() => setStatus("in_stock")}
          className={cn(
            "w-full min-w-0 text-left",
            activeStatus === "in_stock" && "ring-2 ring-primary/20 rounded-[1.25rem]",
          )}
        >
          <KpiCard label="In stock" value={summary?.inStock ?? 0} icon={<RiCheckboxCircleLine />} />
        </button>
        <button
          type="button"
          onClick={() => setStatus("low_stock")}
          className={cn(
            "w-full min-w-0 text-left",
            activeStatus === "low_stock" && "ring-2 ring-primary/20 rounded-[1.25rem]",
          )}
        >
          <KpiCard label="Low stock" value={summary?.lowStock ?? 0} icon={<RiAlertLine />} />
        </button>
        <button
          type="button"
          onClick={() => setStatus("out_of_stock")}
          className={cn(
            "w-full min-w-0 text-left",
            activeStatus === "out_of_stock" && "ring-2 ring-primary/20 rounded-[1.25rem]",
          )}
        >
          <KpiCard label="Out of stock" value={summary?.outOfStock ?? 0} icon={<RiErrorWarningLine />} />
        </button>
      </KpiGrid>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-[#F5F5F3] p-1">
          <TabsTrigger value="stock" className="rounded-full px-4">
            On hand
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-full px-4">
            Activity
          </TabsTrigger>
        </TabsList>
        <TabsContent value="stock" forceMount className={tab === "stock" ? "mt-4" : "hidden"}>
          <InventoryList onSummaryChange={handleSummaryChange} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <InventoryHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}
